import React, { useEffect, useRef } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Button,
  CardFlags,
  Loading,
  NavBar,
  PaymentForm,
  UserInformationForm,
} from "components";
import { id_establishment_default } from "config/establishment";
import usePayment from "hooks/usePayment";
import { useWhitelabel } from "hooks/useWhitelabel";
import { SdkPt } from "models";
import {
  EnumOriginPayment,
  EnumTypeDebit,
  MappedDebitsToGetInstallments,
} from "services/CreditCard";
import { useGetWhiteLabel } from "services/ParcelamosService";
import { Search } from "./Search";
import { EnumStages } from "./types";

import Order1 from "assets/images/icons/order-1.svg";
import Order2 from "assets/images/icons/order-2.svg";
import Order3 from "assets/images/icons/order-3.svg";
import PaymentCheckout from "components/PaymentForm/components/PaymentCheckout";
import { useIsMutating } from "react-query";
import PaymentSummary from "components/PaymentForm/components/ResumeSummary";
import { GetAllDebitsRequest } from "services";
import * as S from "./styles";

export const Partner = () => {
  const { id_usuario } = useParams();
  const { client } = useWhitelabel();
  const {
    paymentForm,
    setPaymentForm,
    dataPaymentBillet,
    getInstallments,
    clearPaymentBilletData,
  } = usePayment();
  const [sdkPT, setSdkPT] = React.useState<SdkPt>({} as SdkPt);
  const [tokenRecaptcha, setTokenRecaptcha] = React.useState<string | null>(
    null
  );
  const [buttonDisabled, setButtonDisabled] = React.useState<boolean>(false);
  const lastVariables = useRef<GetAllDebitsRequest | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeMutatingInstallments = useIsMutating({ mutationKey: "get-installments" })

  const { isLoading } = useGetWhiteLabel(
    id_usuario && !client?.id_usuario ? id_usuario : ""
  );

  const { state } = useLocation();

  const [stage, setStage] = React.useState<EnumStages>(EnumStages.SEARCH);

  const getIconStage = (stage: EnumStages) =>
    [Order1, Order2, Order3][stage - 1];

  const goBack = () => {
    setStage(stage - 1);
  };

  function handleChangeStage(nextStage: EnumStages) {
    if (nextStage === EnumStages.PAYMENT_FORM) {
      const debits: MappedDebitsToGetInstallments = dataPaymentBillet.debits
        .filter((debit) => !debit?.redirect_to_be_paid)
        .map((debit) => ({
          type: EnumTypeDebit.CAR_DEBIT,
          id_debit: debit.id_debit as string,
        }));

      const request = {
        idEstablishment: id_usuario,
        data: { origin: EnumOriginPayment.ONLINE, debits },
      };

      getInstallments(request, {
        onError(error: any) {
          toast.error(error?.response?.data?.message);
        },
      }).then(() => {
        setStage(nextStage);
      });
      return;
    }

    setStage(nextStage);
  }

  function handleSetVariables(variables: GetAllDebitsRequest) {
    lastVariables.current = variables;
  }

  useEffect(() => {
    if (client?.name === "MUNICIPIO DE SÃO BERNARDO DO CAMPO") {
      setSearchParams({ ...searchParams, public_department: "SBC" })
    }
  }, [client]);

  useEffect(() => {
    if (state) {
      handleChangeStage(state.stage);
    }
  }, [state]);

  useEffect(() => {
    return clearPaymentBilletData;
  }, []);

  return (
    <Loading isLoading={isLoading} size="large">
      <NavBar />

        <S.Wrapper id="wrapper">
          <S.Button>
            {stage === EnumStages.SEARCH ||
              stage === EnumStages.ORDER_DETAILS ||
              stage === EnumStages.PAYMENT_METHODS ? (
              <></>
            ) : (
              <Button
                title={`Voltar para etapa`}
                type_button="outlined"
                icon_right={getIconStage(stage - 1)}
                onClick={goBack}
                disabled={buttonDisabled}
              />
            )}
          </S.Button>
        </S.Wrapper>

        <S.WrapperStages>
          <Search
            setStage={handleChangeStage}
            enable={stage === EnumStages.SEARCH}
            variables={lastVariables.current}
            setVariables={(variables) => handleSetVariables(variables as GetAllDebitsRequest)}
            idEstablishment={
              id_usuario ?? client?.id_usuario ?? id_establishment_default
            }
          />
          <UserInformationForm
            enable={stage === EnumStages.CLIENT_IDENTIFICATION}
            setStage={handleChangeStage}
            stage={stage}
          />
          <PaymentForm
            setStage={handleChangeStage}
            paymentForm={paymentForm}
            setPaymentForm={setPaymentForm}
            enable={stage === EnumStages.PAYMENT_FORM}
            setSdkPT={setSdkPT}
          />
          {stage === EnumStages.SUMMARY && (
            <PaymentSummary
              route="parceiro"
              stage={stage}
              onBack={goBack}
              nextStage={() => handleChangeStage(EnumStages.PAYMENT_METHODS)}
              setTokenRecaptcha={setTokenRecaptcha}
              tokenRecaptcha={tokenRecaptcha}
              isLoadingInstallments={!!activeMutatingInstallments}
            />
          )}
          {stage === EnumStages.PAYMENT_METHODS && (
            <PaymentCheckout
              stage={stage}
              route="parceiro"
              tokenRecaptcha={tokenRecaptcha}
              setTokenRecaptcha={setTokenRecaptcha}
            />
          )}
          <CardFlags />
        </S.WrapperStages>
    </Loading>
  );
};
